import { AdminApi, ApiAdminSetNewSubscriptionLevelPostRequest, WebApiAdminDtoCreateUserDto, WebApiAdminDtoInvoiceGenerationRequest, WebApiAdminDtoSetSubscriptionDto, WebApiPaymentRepositoryPingPaymentsDtoTenantUpdateTenantRequest } from "../Repository/eventbookyapi";
import { WebApiAdminDtoSetLoginFailedCountDto, WebApiAdminDtoSetUserPasswordDto, WebApiAdminDtoSetInvoiceStatusDto } from "../Repository/eventbookyapi";
import { InvoiceStatus } from "../Types/InvoiceStatus";
import { GetApiConfiguration } from "./ServiceConfiguration";
import { DashboardService } from "./DashboardService";

export abstract class AdminService {
    public static CreateCompany = async (request: WebApiAdminDtoCreateUserDto) => {
        return new AdminApi(GetApiConfiguration()).apiAdminCreateUserPost({
            webApiAdminDtoCreateUserDto: request
        });
    };

    public static GetAllUsers = async () => {
        return new AdminApi(GetApiConfiguration()).apiAdminGetAllUsersGet();
    };

    public static SetUserAsAdmin = async (userId: number, companyId: number) => {
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include' as RequestCredentials,
            body: JSON.stringify({ userId, companyId })
        };
        const response = await fetch(process.env.REACT_APP_API_URL + '/Admin/SetUserAsAdmin', requestOptions);
        return response.json();
    };

    public static SearchMembers = async (companyId: number, query: string) => {
        return new AdminApi(GetApiConfiguration()).apiAdminSearchMembersGet({ companyId, query: query || undefined });
    };

    // Sets the public site auth cookie for the member and returns the company's public site url
    public static LoginAsMember = async (companyId: number, memberId: number) => {
        return new AdminApi(GetApiConfiguration()).apiAdminLoginAsMemberPost({
            webApiAdminDtoLoginAsMemberDto: { companyId, memberId }
        });
    };

    public static DeleteAllCompanyMembers = async (companyId: number) => {
        return new AdminApi(GetApiConfiguration()).apiAdminDeleteAllMembersDelete({ companyId: companyId });
    };

    public static DeleteAllMemberFields = async (companyId: number) => {
        return new AdminApi(GetApiConfiguration()).apiAdminDeleteAllMemberFieldsDelete({ companyId: companyId });
    };

    public static SetSubscription = async (companyId: number, subscription: WebApiAdminDtoSetSubscriptionDto) => {
        const body: ApiAdminSetNewSubscriptionLevelPostRequest = { webApiAdminDtoSetSubscriptionDto: subscription, companyId: companyId };
        return new AdminApi(GetApiConfiguration()).apiAdminSetNewSubscriptionLevelPost(body);
    };

    public static GetDashboard = async () => {
        return new AdminApi(GetApiConfiguration()).apiAdminDashboardGet();
    };

    public static GetTenant = async () => {
        return new AdminApi(GetApiConfiguration()).apiAdminGetTenantGet();
    };

    public static UpdateTenant = async (request: WebApiPaymentRepositoryPingPaymentsDtoTenantUpdateTenantRequest) => {
        return new AdminApi(GetApiConfiguration()).apiAdminUpdateTenantPut({
            webApiPaymentRepositoryPingPaymentsDtoTenantUpdateTenantRequest: request,
        });
    };

    public static SetLoginFailedCount = async (userId: number, loginFailedCount: number) => {
        const body: WebApiAdminDtoSetLoginFailedCountDto = { userId, loginFailedCount };
        return new AdminApi(GetApiConfiguration()).apiAdminSetLoginFailedCountPost({
            webApiAdminDtoSetLoginFailedCountDto: body
        });
    };

    public static SetUserPassword = async (userId: number, newPassword: string) => {
        const body: WebApiAdminDtoSetUserPasswordDto = { userId, newPassword };
        return new AdminApi(GetApiConfiguration()).apiAdminSetUserPasswordPost({
            webApiAdminDtoSetUserPasswordDto: body
        });
    };

    public static GetInvoiceFormDefaults = async (companyId: number) => {
        return new AdminApi(GetApiConfiguration()).apiAdminGetInvoiceFormDefaultsGet({ companyId });
    };

    public static GetCustomerInvoices = async () => {
        return new AdminApi(GetApiConfiguration()).apiAdminGetCustomerInvoicesGet();
    };

    public static SetInvoiceStatus = async (invoiceId: number, status: InvoiceStatus) => {
        const body: WebApiAdminDtoSetInvoiceStatusDto = { invoiceId, status };
        return new AdminApi(GetApiConfiguration()).apiAdminSetInvoiceStatusPost({
            webApiAdminDtoSetInvoiceStatusDto: body
        });
    };

    public static GenerateInvoicePdf = async (request: WebApiAdminDtoInvoiceGenerationRequest): Promise<void> => {
        const response = await new AdminApi(GetApiConfiguration()).apiAdminGenerateInvoicePdfPostRaw({
            webApiAdminDtoInvoiceGenerationRequest: request
        });
        const blob = await response.raw.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Invoice_${request.invoiceNumber}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    };

    public static DownloadInvoicePdf = async (invoiceId: number, invoiceNumber?: string | null): Promise<void> => {
        const response = await new AdminApi(GetApiConfiguration()).apiAdminDownloadInvoicePdfGetRaw({ invoiceId });
        const blob = await response.raw.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Invoice_${invoiceNumber ?? invoiceId}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    };

    public static GetPingPaymentFeeReport = async (fromDate?: string, toDate?: string) => {
        const url = new URL(`${process.env.REACT_APP_API_URL_BASE}/api/Admin/GetPingPaymentFeeReport`);
        if (fromDate) url.searchParams.set('fromDate', fromDate);
        if (toDate) url.searchParams.set('toDate', toDate);
        const response = await fetch(url.toString(), { credentials: 'include' });
        return response.json();
    };

    public static DownloadPingPaymentFeeReportPdf = async (fromDate?: string, toDate?: string) => {
        const url = new URL(`${process.env.REACT_APP_API_URL_BASE}/api/Admin/GetPingPaymentFeeReportPdf`);
        if (fromDate) url.searchParams.set('fromDate', fromDate);
        if (toDate) url.searchParams.set('toDate', toDate);
        const response = await fetch(url.toString(), { credentials: 'include' });
        return response.blob();
    };
}